import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTour } from '@reactour/tour';
import ReactSVG from 'react-svg';
import * as lib from '~/dependencies';
import { isExtension, eventTracker } from '~/helpers';
import { useStatus, useTryToCall } from '~/hooks';
import {
  Contacts,
  Teams,
  History,
  PhoneIcon,
  DotsMenuIcon,
  MessagesIcon,
} from '~/assets/svgIcons';
import * as actions from '~/store/actions';
import {
  HistoryBadge,
  BottomNavigationComponent,
  ConverstaionsBadge,
} from './styles';

// Checking in which environment are the app running
const STAGING_ENV = ['djhk1kho598u6.cloudfront.net'];
const DEVELOP_ENV = ['d1gaikb7mx9zak.cloudfront.net', 'localhost'];

export default function BottomNav(props) {
  // PROPS
  const { match, history } = props;

  // REFs
  const btnCall = useRef();

  // STATES
  const [badge, setBadge] = useState(0);

  // HOOKS
  const { t } = lib.useTranslation();
  const dispatch = useDispatch();
  const tryToCall = useTryToCall();
  const [{ statusColor }, setStatus] = useStatus();
  const { setIsOpen } = useTour();

  // SELECTORS
  const isTourCompleted = useSelector(
    state => state?.accountInfo?.tourCompleted
  );
  const statusSys = useSelector(state => state.system && state.system.status);
  const dev = useSelector(state => state.system && state.system.dev);
  const currentCalls = useSelector(
    state => state.callCurrent && state.callCurrent.data
  );
  const timeZone = useSelector(state => state.auth?.userTimeZone);
  const userCountry = useSelector(state => state.auth?.userCountry);
  const lastHistoryCheck = useSelector(state => state.system.lastHistoryCheck);
  const numberTo = useSelector(state => state.system.numberToTransfer);
  const lastDialedNumber = useSelector(state => state.system.lastDialedNumber);
  const phoneCallLogsMy = useSelector(state => state.call.phoneCallLogsMy);
  const unreadMessagesCount = useSelector(
    state => state.conversation.unreadMessagesCount
  );
  const { temporaryCountryCode, callerIdCountryCode } = useSelector(
    state => state.system
  );

  // CONST's
  const myCallLogs = phoneCallLogsMy.flatMap(e => e.data);
  const isQAStage = STAGING_ENV.includes(window.location.hostname);
  const isDevelop = DEVELOP_ENV.includes(window.location.hostname);
  const isDtmfActive = currentCalls
    ? !!currentCalls.filter(e => e.dtmf === true && e.finished === false).length
    : false;

  useEffect(() => {
    if (!isTourCompleted) {
      setTimeout(() => {
        setIsOpen(true);
      }, 1000);
    }
  }, [isTourCompleted, setIsOpen]);

  // EFFECTS
  useEffect(() => {
    setStatus(statusSys);
  }, [setStatus, statusSys]);

  function handleClickCenterCallButton() {
    if (numberTo && currentCalls?.length < 2 && !btnCall.current.disabled) {
      btnCall.current.disabled = true;
      if (!isExtension(numberTo) && !numberTo.startsWith('*')) {
        tryToCall(
          `00${temporaryCountryCode || callerIdCountryCode}${numberTo}`
        );
      } else {
        tryToCall(numberTo);
      }
    } else {
      dispatch(actions.numberToTransfer(lastDialedNumber));
    }
  }

  useEffect(() => {
    if (!timeZone || !myCallLogs?.length) return undefined;

    // Statuses which can be only be considered as missed calls
    const MISSED_CALL_STATUSES = ['NO ANSWER', 'BLOCKED'];

    const isHistoryPathname = history.location.pathname.match(/^\/app(\/)?$/);
    const lastHistoryCheckTzBased = lib.dayjs(lastHistoryCheck).tz(timeZone);

    const missedCalls = myCallLogs.filter(
      call =>
        MISSED_CALL_STATUSES.includes(call.status) &&
        lib.dayjs(call.callDate).diff(lastHistoryCheckTzBased, 'seconds') >= 0
    );

    const missedCallsCount = missedCalls.length;

    // Update badge count
    setBadge(missedCallsCount);

    // Clear badge after delay when user is on history page and window is focused
    if (missedCallsCount > 0 && isHistoryPathname && document.hasFocus()) {
      const timeoutId = setTimeout(() => {
        setBadge(0);
      }, 12000);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [
    myCallLogs,
    lastHistoryCheck,
    history.location.pathname,
    timeZone,
    userCountry,
  ]);

  const renderMissedCallsCountBadge = () => {
    if (badge > 0) {
      return (
        <HistoryBadge badgeContent={badge} color="secondary">
          <ReactSVG src={History} className="icon" />
        </HistoryBadge>
      );
    }
    return <ReactSVG src={History} className="icon" />;
  };

  return (
    <BottomNavigationComponent block={currentCalls?.length >= 2}>
      <div
        style={{
          position: 'absolute',
          display: 'flex',
          flexDirection: 'row',
          left: 0,
        }}>
        {isQAStage && (
          <div className="devMode" style={{ backgroundColor: '#FFA500' }}>
            <span>ENV: QA</span>
          </div>
        )}

        {dev && (
          <div className="devMode">
            <span>Developer mode</span>
          </div>
        )}

        {isDevelop && (
          <div className="devMode" style={{ backgroundColor: '#FF5050' }}>
            <span>ENV: Develop</span>
          </div>
        )}
      </div>

      <lib.NavLink
        exact
        to={`${match.path}/teams`}
        activeClassName="selected"
        data-test-id="nav-item-team"
        onClick={() => eventTracker(events => events.TeamsButtonClicked)}>
        <div className="nav-item">
          <ReactSVG src={Teams} className="icon" />
          <span>{t('components.bottomNav.team')}</span>
        </div>
      </lib.NavLink>
      <lib.NavLink exact to={`${match.path}/`} activeClassName="selected">
        <div
          role="presentation"
          className="nav-item"
          onClick={() => dispatch(actions.setLastHistoryCheck())}>
          {renderMissedCallsCountBadge()}
          <span>{t('components.bottomNav.history')}</span>
        </div>
      </lib.NavLink>

      <div className="nav-item center">
        {history.location.pathname === '/app/dialPad' && (
          <button
            type="button"
            className="call-button"
            ref={btnCall}
            onClick={handleClickCenterCallButton}
            disabled={btnCall.current?.disabled || currentCalls?.length >= 2}>
            <div className="border-one">
              <div className="border-two">
                <ReactSVG src={PhoneIcon} className="icon" />
              </div>
            </div>
          </button>
        )}
        {history.location.pathname !== '/app/dialPad' && (
          <lib.Link
            to={`${match.path}/dialPad`}
            className={
              (currentCalls?.length && !currentCalls[0]?.answered) ||
              isDtmfActive
                ? 'call-button disabled'
                : 'call-button'
            }
            // NWA-2605 Disabled Dialer Click Tracking due to MixPanel not registering the event and causing performance issues.
            // onClick={() => eventTracker(events => events.DialerClicked)}
          >
            <div className="border-one">
              <div className="border-two">
                <ReactSVG src={DotsMenuIcon} className="icon" />
              </div>
            </div>
          </lib.Link>
        )}
      </div>

      <lib.NavLink
        exact
        to={`${match.path}/contacts`}
        activeClassName="selected"
        onClick={() => eventTracker(events => events.ContactsClicked)}>
        <div className="nav-item">
          <ReactSVG src={Contacts} className="icon" />
          <span>{t('components.bottomNav.contacts')}</span>
        </div>
      </lib.NavLink>
      <lib.NavLink
        exact
        to={`${match.path}/conversations`}
        onClick={() => eventTracker(events => events.ConversationsClicked)}
        activeClassName="selected">
        <div className="nav-item">
          <ConverstaionsBadge
            className="conversations-react-tour"
            overlap="circular"
            bgcolor={statusColor}
            badgeContent={unreadMessagesCount || null}
            showZero
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}>
            <ReactSVG src={MessagesIcon} className="icon" />
          </ConverstaionsBadge>
          <span>{t('components.bottomNav.messages')}</span>
        </div>
      </lib.NavLink>
    </BottomNavigationComponent>
  );
}

BottomNav.defaultProps = {
  match: [],
  history: [],
  location: [],
};

BottomNav.propTypes = {
  match: lib.PropTypes.instanceOf(Object),
  history: lib.PropTypes.instanceOf(Object),
  location: lib.PropTypes.instanceOf(Object),
};
